import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Phone, ChevronLeft, Video, Mail, MessageSquare, PhoneOff, Mic, MicOff, Volume2, Plus, Grid, Users, Search } from 'lucide-react';
import { retellService } from '../services/retellService';
import './RetellContactsDemo.css';

// Mock data as fallback
const mockAgents = [
  { id: '1', name: 'Autometa Agent',        initials: 'AA', role: 'General receptionist',  type: 'Voice Agent' },
  { id: '2', name: 'BrewNation Café',  initials: 'BC', role: 'Takes coffee orders',    type: 'Voice Agent' },
  { id: '3', name: 'Elite Cuts Barbershop', initials: 'EB', role: 'Books haircuts 24/7',    type: 'Voice Agent' },
  { id: '4', name: 'NextKey Realty',        initials: 'NR', role: 'Schedules viewings',     type: 'Voice Agent' },
  { id: '5', name: 'PrimeCare Clinic',      initials: 'PC', role: 'Patient appointments',   type: 'Voice Agent' },
];

// A brief, contact-specific description for the detail "notes" field. Matched by the
// agent's business type (works for both the live Retell agents and the mock fallback),
// with a generic 24/7 blurb for anything we don't recognise.
const AGENT_DESCRIPTIONS = [
  { match: /autometa/i,                       text: "Answers questions about AutoMeta, books a consultation, and texts a confirmation." },
  { match: /brew|caf[eé]|coffee/i,            text: "Takes coffee and food orders, shares the menu and prices, and texts an order confirmation." },
  { match: /cut|barber|salon|hair|grooming/i, text: "Books haircuts, fades and beard trims, and texts a confirmation." },
  { match: /realty|real ?estate|homes|propert/i, text: "Helps with buying or renting, sets up tours, and texts a confirmation." },
  { match: /care|clinic|health|dental|medical|primecare/i, text: "Books and checks appointments, and texts a confirmation." },
  { match: /resta|kitchen|grill|pizza|diner|bistro/i, text: "Takes reservations and to-go orders, shares the menu, and texts a confirmation." },
];

function descriptionFor(agent) {
  if (!agent) return '';
  if (agent.description) return agent.description;
  const hay = `${agent.name || ''} ${agent.role || ''}`;
  const hit = AGENT_DESCRIPTIONS.find((d) => d.match.test(hay));
  if (hit) return hit.text;
  return 'Answers calls anytime, books appointments, and texts a confirmation.';
}

// iOS status bar (time + signal/wifi/battery) — shared so the contacts list and the
// contact-detail screen show the same phone chrome.
function StatusBar({ time }) {
  return (
    <div className="ios-header-top">
      <span className="ios-time">{time}</span>
      <div className="ios-status-icons" aria-hidden="true">
        <svg viewBox="0 0 17 12" fill="none">
          <rect x="0" y="8" width="3" height="4" rx="0.5" fill="currentColor"/>
          <rect x="5" y="6" width="3" height="6" rx="0.5" fill="currentColor"/>
          <rect x="10" y="3" width="3" height="9" rx="0.5" fill="currentColor"/>
          <rect x="15" y="0" width="3" height="12" rx="0.5" fill="currentColor" opacity="0.3"/>
        </svg>
        <svg viewBox="0 0 15 11" fill="none">
          <path d="M7.5 10.5C8.05228 10.5 8.5 10.0523 8.5 9.5C8.5 8.94772 8.05228 8.5 7.5 8.5C6.94772 8.5 6.5 8.94772 6.5 9.5C6.5 10.0523 6.94772 10.5 7.5 10.5Z" fill="currentColor"/>
          <path d="M4.5 6.5C5.5 5.5 6.5 5 7.5 5C8.5 5 9.5 5.5 10.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M2 3.5C3.5 2 5.5 1 7.5 1C9.5 1 11.5 2 13 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <svg className="ios-battery" viewBox="0 0 27 13" fill="none">
          <rect x="0.5" y="1.5" width="22" height="10" rx="2.5" stroke="currentColor" strokeOpacity="0.35"/>
          <rect x="24" y="4.5" width="2.5" height="4" rx="1" fill="currentColor" fillOpacity="0.4"/>
          <rect x="2" y="3" width="18" height="7" rx="1.5" fill="currentColor"/>
        </svg>
      </div>
    </div>
  );
}

const RetellContactsDemo = forwardRef(({ onCallStart, showNotification = false }, ref) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const searchTerm = '';
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [callStatus, setCallStatus] = useState('idle');
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [, setIsUserSpeaking] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const [showNotif, setShowNotif] = useState(false);
  const callTimerRef = useRef(null);

  // Show notification after phone loads
  useEffect(() => {
    if (showNotification && agents.length > 0 && !loading) {
      setTimeout(() => {
        setShowNotif(true);
        setTimeout(() => setShowNotif(false), 4000);
      }, 2000);
    }
  }, [showNotification, agents.length, loading]);

  // iOS status-bar clock: "9:41" style (no AM/PM) so it fits the left ear
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      let hours = now.getHours();
      const minutes = now.getMinutes().toString().padStart(2, '0');
      hours = hours % 12;
      hours = hours ? hours : 12;
      setCurrentTime(`${hours}:${minutes}`);
    };

    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadAgents = async () => {
      setLoading(true);
      const apiAgents = await retellService.fetchAgents();

      if (apiAgents && apiAgents.length > 0) {
        // Sort agents alphabetically by name
        const sortedAgents = apiAgents.sort((a, b) => a.name.localeCompare(b.name));
        // Remove duplicates
        const uniqueAgents = sortedAgents.filter((agent, index, self) =>
          index === self.findIndex((a) => a.name === agent.name)
        );
        setAgents(uniqueAgents);
      } else {
        setAgents(mockAgents);
      }
      setLoading(false);
    };

    loadAgents();

    return () => {
      retellService.stopCall();
      if (callTimerRef.current) clearInterval(callTimerRef.current);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    tryFirstAgent: () => {
      if (agents.length > 0) {
        const agent = agents[0];
        setSelectedAgent(agent);
        // Small delay so the detail screen renders, then start the call
        setTimeout(() => {
          startCallForAgent(agent);
        }, 600);
      }
    },
  }), [agents]);

  const handleAgentClick = (agent) => {
    setSelectedAgent(agent);
  };

  const handleBack = () => {
    if (callStatus === 'idle' || callStatus === 'ended') {
      setSelectedAgent(null);
      setCallStatus('idle');
      setCallDuration(0);
    }
  };

  const startCallForAgent = async (agent) => {
    if (!agent) return;

    setCallStatus('calling');
    setCallDuration(0);
    setIsMuted(false);
    if (onCallStart) onCallStart(agent);

    try {
      const callData = await retellService.createWebCall(agent.id);
      setCallStatus('connecting');
      await retellService.startCall(callData);

      setCallStatus('active');
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);

      retellService.on('agent_start_talking', () => setIsAgentSpeaking(true));
      retellService.on('agent_stop_talking', () => setIsAgentSpeaking(false));
      retellService.on('user_start_talking', () => setIsUserSpeaking(true));
      retellService.on('user_stop_talking', () => setIsUserSpeaking(false));
      retellService.on('call_ended', () => endCall());
      retellService.on('error', () => endCall());

    } catch (error) {
      endCall();
    }
  };

  const startCall = () => startCallForAgent(selectedAgent);

  const toggleMute = () => {
    if (callStatus === 'active') {
      setIsMuted(!isMuted);
    }
  };

  const endCall = () => {
    retellService.stopCall();
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    setCallStatus('ended');
    setIsAgentSpeaking(false);
    setIsUserSpeaking(false);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const filteredAgents = agents.filter(agent =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedAgents = filteredAgents.reduce((groups, agent) => {
    const firstLetter = agent.name[0].toUpperCase();
    if (!groups[firstLetter]) groups[firstLetter] = [];
    groups[firstLetter].push(agent);
    return groups;
  }, {});

  const sortedLetters = Object.keys(groupedAgents).sort();

  // Cycle colorful gradients for avatars so the list pops
  const avatarPalette = [
    'linear-gradient(135deg, #10b981 0%, #14B8A6 100%)', // green/teal
    'linear-gradient(135deg, #f97316 0%, #fb923c 100%)', // orange
    'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)', // cyan/blue
    'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)', // pink
    'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)', // purple
    'linear-gradient(135deg, #facc15 0%, #f59e0b 100%)', // amber
  ];
  const colorFor = (key) => {
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
    return avatarPalette[h % avatarPalette.length];
  };

  if (!selectedAgent) {
    return (
      <div className="ios-contacts-container">
        {showNotif && (
          <div className="ios-notification">
            <div className="notif-icon">📞</div>
            <div className="notif-content">
              <div className="notif-app">AutoMeta Demo</div>
              <div className="notif-message">Tap any agent to try a live call</div>
            </div>
          </div>
        )}
        <div className="ios-header">
          <StatusBar time={currentTime} />
          <div className="ios-title-row">
            <h2 className="ios-title">Contacts</h2>
            <span className="ios-live-badge">
              <span className="ios-live-dot"></span> LIVE
            </span>
          </div>
          <div className="ios-search-bar">
            <Search size={14} className="ios-search-icon" />
            <span className="ios-search-placeholder">Search</span>
          </div>
          <div className="ios-tap-pill">
            <Phone size={11} fill="currentColor" />
            <span>Say Hello to Instant AI Calls.</span>
          </div>
        </div>

        <div className="ios-list-container">
          {loading ? (
            <div className="ios-loading">Loading...</div>
          ) : (
            <>
              {sortedLetters.map(letter => (
                <React.Fragment key={letter}>
                  <div className="ios-section-header">{letter}</div>
                  {groupedAgents[letter].map((agent, index) => (
                    <div key={`${agent.id}-${index}`} className="ios-contact-item" onClick={() => handleAgentClick(agent)}>
                      <div className="ios-contact-avatar" style={{ background: colorFor(agent.id + agent.name) }}>{agent.initials}</div>
                      <div className="ios-contact-info">
                        <span className="ios-name">{agent.name}</span>
                      </div>
                      <button
                        className="ios-row-call"
                        aria-label={`Call ${agent.name}`}
                        onClick={(e) => { e.stopPropagation(); setSelectedAgent(agent); startCallForAgent(agent); }}
                      >
                        <Phone size={14} fill="currentColor" />
                      </button>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`ios-detail-container ${callStatus !== 'idle' ? 'in-call' : ''}`}>
      {callStatus === 'idle' && (
        <>
          <StatusBar time={currentTime} />
          <div className="ios-detail-nav">
            <button className="ios-back-btn" onClick={handleBack}>
              <ChevronLeft size={20} /> Contacts
            </button>
            <span className="ios-edit-btn">Edit</span>
          </div>

          <div className="ios-profile-header">
            <div className="ios-profile-avatar" style={{ background: colorFor(selectedAgent.id + selectedAgent.name) }}>{selectedAgent.initials}</div>
            <h2 className="ios-profile-name">{selectedAgent.name}</h2>
            <span className="ios-profile-role">{selectedAgent.role}</span>

            <div className="ios-action-row">
              <button className="ios-action-btn message" disabled><MessageSquare size={16} /><span>message</span></button>
              <button className="ios-action-btn call" onClick={startCall}><Phone size={16} fill="currentColor" /><span>call</span></button>
              <button className="ios-action-btn video" disabled><Video size={16} /><span>video</span></button>
              <button className="ios-action-btn mail" disabled><Mail size={16} /><span>mail</span></button>
            </div>
          </div>

          <div className="ios-info-section">
            <div className="ios-info-row">
              <span className="ios-label">type</span>
              <span className="ios-value">Voice Assistant</span>
            </div>
            <div className="ios-info-row">
              <span className="ios-label">notes</span>
              <span className="ios-value">{descriptionFor(selectedAgent)}</span>
            </div>
          </div>
        </>
      )}

      {(callStatus === 'calling' || callStatus === 'connecting' || callStatus === 'active') && (
        <div className="ios-call-screen">
          <div className="ios-call-top-info">
            <h2 className="ios-call-name">{selectedAgent.name}</h2>
            <span className="ios-call-status-text">
              {callStatus === 'active' ? formatDuration(callDuration) : 'calling...'}
            </span>
          </div>

          <div className="ios-call-center">
            <div className={`ios-call-avatar ${isAgentSpeaking ? 'agent-talking' : ''}`}>
              {selectedAgent.initials}
            </div>
          </div>

          <div className="ios-call-bottom-controls">
            <div className="ios-call-grid">
              <div className="ios-grid-item">
                <button className={`ios-circle-btn ${isMuted ? 'active' : ''}`} onClick={toggleMute}>
                  {isMuted ? <MicOff size={22} /> : <Mic size={22} />}
                </button>
                <span>mute</span>
              </div>
              <div className="ios-grid-item">
                <button className="ios-circle-btn disabled"><Grid size={22} /></button>
                <span>keypad</span>
              </div>
              <div className="ios-grid-item">
                <button className="ios-circle-btn disabled"><Volume2 size={22} /></button>
                <span>speaker</span>
              </div>
              <div className="ios-grid-item">
                <button className="ios-circle-btn disabled"><Plus size={22} /></button>
                <span>add call</span>
              </div>
              <div className="ios-grid-item">
                <button className="ios-circle-btn disabled"><Video size={22} /></button>
                <span>FaceTime</span>
              </div>
              <div className="ios-grid-item">
                <button className="ios-circle-btn disabled"><Users size={22} /></button>
                <span>contacts</span>
              </div>
            </div>

            <div className="ios-end-call-row">
              <button className="ios-end-call-btn" onClick={endCall}>
                <PhoneOff size={26} />
              </button>
            </div>
          </div>
        </div>
      )}

      {callStatus === 'ended' && (
        <div className="ios-call-ended-screen">
          <div className="summary-card">
            <span className="summary-label">Call Duration</span>
            <span className="summary-value">{formatDuration(callDuration)}</span>
          </div>
          <button className="ios-done-btn" onClick={handleBack}>Done</button>
        </div>
      )}
    </div>
  );
});

RetellContactsDemo.displayName = 'RetellContactsDemo';
export default RetellContactsDemo;
